import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthContext from "../../context/AuthContext";
import Fetch from "../../services/Fetch";
import PageContent from "../../components/PageContent";
import { useConstants } from "../../hooks/UseConstants";
import useSnackBar from "../../hooks/UseSnackBar";
import SnackbarAlert from "../../components/SnackBar";
import { useWaits } from "../../hooks/UseWait";
import { Box, Button, Chip, CircularProgress, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, useTheme, Alert } from "@mui/material";
import DashboardStatCard from "../../components/cards/DashboardStatCard";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import PageHero from "../../components/motion/PageHero";
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { formatCurrency } from "../../helper/formatCurrency";
import { parsePaginatedResponse } from "../../utils/parsePaginatedResponse";

function extractApiError(data) {
    if (!data) return '';
    if (Array.isArray(data.errors) && data.errors.length) return String(data.errors[0]);
    if (typeof data.message === 'string' && data.message.trim()) return data.message.trim();
    return '';
}

function formatDate(value, isAr) {
    if (!value) return isAr ? 'غير متوفر' : 'Unavailable';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);

    return new Intl.DateTimeFormat(isAr ? 'ar-EG' : 'en-US', {
        dateStyle: 'medium',
        timeStyle: 'short',
    }).format(parsed);
}

function Financial() {
    const navigate = useNavigate();
    const theme = useTheme();
    const { host, language } = useConstants();
    const { wait, profile, setProfile } = useContext(AuthContext);
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const { sendWait, setSendWait } = useWaits();
    const [transactions, setTransactions] = useState([]);
    const [amount, setAmount] = useState('');
    const [payoutMethod, setPayoutMethod] = useState('bank_transfer');
    const [payoutDetails, setPayoutDetails] = useState('');
    const [loading, setLoading] = useState(true);
    const [withdrawalRequests, setWithdrawalRequests] = useState([]);

    const load = async () => {
        const [txRes, wrRes, profileRes] = await Promise.all([
            Fetch(host + '/transaction/history?per_page=20', 'GET', null),
            Fetch(host + '/teacher/profit/withdrawal-requests', 'GET', null),
            Fetch(host + '/account/get-profile', 'GET', null),
        ]);
        if (profileRes.status === 200 && profileRes.data?.data) {
            setProfile(profileRes.data.data);
        }
        if (txRes.status === 200) {
            const txs = txRes.data?.data || txRes.data || [];
            setTransactions(Array.isArray(txs) ? txs : []);
        }
        if (wrRes.status === 200) {
            const { items } = parsePaginatedResponse(wrRes);
            setWithdrawalRequests(items);
        } else {
            setWithdrawalRequests([]);
        }
        setLoading(false);
    };

    useEffect(() => {
        if (!wait) load();
    }, [wait]);

    const balance = Number(profile?.wallet?.balance ?? 0);
    const isAr = language === 'ar';
    const pendingRequests = withdrawalRequests.filter((item) => item.status === 'pending');
    const pendingRequestsCount = pendingRequests.length;
    const hasPendingRequest = pendingRequestsCount > 0;

    const mapWithdrawError = (msg) => {
        if (!msg) return isAr ? 'تعذر إرسال طلب السحب' : 'Could not submit withdrawal request';
        const text = String(msg);
        const lower = text.toLowerCase();
        if (lower.includes('insufficient') || text.includes('رصيد غير كاف')) {
            return isAr
                ? `رصيد غير كافٍ. الرصيد المتاح للسحب: ${formatCurrency(balance, isAr)}`
                : `Insufficient balance. Available for withdrawal: ${formatCurrency(balance, isAr)}`;
        }
        if (lower.includes('pending') || text.includes('قيد الانتظار') || text.includes('طلب سحب')) {
            return isAr
                ? 'لديك بالفعل طلب سحب قيد الانتظار. انتظر رد الإدارة قبل إرسال طلب جديد.'
                : 'You already have a pending withdrawal request. Wait for admin review before submitting another.';
        }
        return text;
    };

    const requestWithdraw = async () => {
        const numericAmount = Number(amount);
        if (!numericAmount || numericAmount <= 0) {
            setSnackBar('error', isAr ? 'أدخل مبلغاً صحيحاً أكبر من صفر' : 'Enter a valid amount greater than zero');
            return;
        }
        if (hasPendingRequest) {
            setSnackBar('error', isAr ? 'لديك بالفعل طلب سحب قيد الانتظار. انتظر رد الإدارة قبل إرسال طلب جديد.' : 'You already have a pending withdrawal request. Wait for admin review before submitting another.');
            return;
        }
        if (numericAmount > balance) {
            setSnackBar('error', isAr
                ? `رصيد غير كافٍ. الرصيد المتاح للسحب: ${formatCurrency(balance, isAr)}`
                : `Insufficient balance. Available for withdrawal: ${formatCurrency(balance, isAr)}`);
            return;
        }

        setSendWait(true);
        const formData = new FormData();
        formData.append('amount', String(numericAmount));
        formData.append('payout_method', payoutMethod);
        if (payoutDetails.trim()) {
            formData.append('payout_details', payoutDetails.trim());
        }
        const result = await Fetch(host + '/teacher/profit/withdraw-request', 'POST', formData);
        if (result.status === 200) {
            setSnackBar('success', language === 'ar' ? 'تم إرسال طلب السحب بنجاح. بانتظار موافقة الإدارة.' : 'Withdrawal request sent. Awaiting admin approval.');
            setAmount('');
            setPayoutMethod('bank_transfer');
            setPayoutDetails('');
            await load();
        } else {
            await load();
            setSnackBar('error', mapWithdrawError(extractApiError(result.data)));
        }
        setSendWait(false);
    };

    const getMethodLabel = (method) => {
        if (!method) return isAr ? 'غير محدد' : 'Not specified';
        if (method === 'wallet') return isAr ? 'محفظة' : 'Wallet';
        if (method === 'cash') return isAr ? 'نقداً' : 'Cash';
        return isAr ? 'تحويل بنكي' : 'Bank transfer';
    };

    const getStatusLabel = (status) => {
        if (status === 'approved') return isAr ? 'موافق عليه' : 'Approved';
        if (status === 'rejected') return isAr ? 'مرفوض' : 'Rejected';
        return isAr ? 'قيد الانتظار' : 'Pending';
    };

    const getTransactionTypeLabel = (type) => {
        if (type === 'credit') return isAr ? 'إيداع / شحن' : 'Credit / Top-up';
        if (type === 'withdraw') return isAr ? 'سحب' : 'Withdrawal';
        if (type === 'debit') return isAr ? 'خصم / شراء' : 'Debit / Purchase';
        return type || (isAr ? 'غير معروف' : 'Unknown');
    };

    const getTransactionTypeColor = (type) => {
        if (type === 'credit') return 'success';
        if (type === 'withdraw') return 'warning';
        if (type === 'debit') return 'error';
        return 'default';
    };

    return (
        <>
            <PageContent wait={wait} loading={loading}>
                <PageHero
                    title={language === 'ar' ? 'المالية' : 'Financial'}
                    subtitle={language === 'ar' ? 'الأرباح وطلبات السحب والمعاملات' : 'Earnings, withdrawals and transactions'}
                    icon={<AccountBalanceWalletOutlinedIcon />}
                />

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2, mb: 3 }}>
                    <Box>
                        <DashboardStatCard
                            label={language === 'ar' ? 'الرصيد المتاح للسحب' : 'Available balance'}
                            value={formatCurrency(balance, isAr)}
                            icon={<AccountBalanceWalletOutlinedIcon />}
                            className="w-full mx-0 max-sm:mx-auto"
                            onClick={() => navigate('/financial')}
                        />
                    </Box>
                    <Box>
                        <DashboardStatCard
                            label={isAr ? 'طلبات قيد الانتظار' : 'Pending requests'}
                            value={pendingRequestsCount}
                            icon={<HourglassEmptyOutlinedIcon />}
                            accent="sky"
                            className="w-full mx-0 max-sm:mx-auto"
                        />
                    </Box>
                    <Box>
                        <DashboardStatCard
                            label={isAr ? 'آخر الحركات المعروضة' : 'Recent movements shown'}
                            value={transactions.length}
                            icon={<ReceiptLongOutlinedIcon />}
                            accent="teal"
                            className="w-full mx-0 max-sm:mx-auto"
                        />
                    </Box>
                </Box>

                <DashboardPanelCard
                    title={language === 'ar' ? 'طلب سحب أرباح' : 'Request withdrawal'}
                    subtitle={language === 'ar' ? 'أدخل المبلغ وحدد طريقة الاستلام حتى يراجع الأدمن طلبك بسرعة.' : 'Enter the amount and payout method so the admin can review your request quickly.'}
                    className="!mb-4 mx-0"
                >
                    {hasPendingRequest ? (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {isAr
                                ? `لديك ${pendingRequestsCount} طلب سحب قيد المراجعة (${formatCurrency(pendingRequests[0]?.amount ?? 0, isAr)}). لا يمكن إرسال طلب جديد حتى يرد الأدمن بالموافقة أو الرفض.`
                                : `You have ${pendingRequestsCount} withdrawal request(s) under review (${formatCurrency(pendingRequests[0]?.amount ?? 0, isAr)}). Submit a new one only after admin approval or rejection.`}
                        </Alert>
                    ) : null}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '160px 220px minmax(260px, 1fr) auto' }, gap: 1.5, alignItems: 'start', opacity: hasPendingRequest || balance <= 0 ? 0.55 : 1 }}>
                            <TextField
                                type="number"
                                label={language === 'ar' ? 'المبلغ' : 'Amount'}
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                size="small"
                                fullWidth
                                inputProps={{ min: 1, max: balance > 0 ? balance : undefined, step: '0.01' }}
                                helperText={balance > 0
                                    ? (isAr ? `الحد الأقصى: ${formatCurrency(balance, isAr)}` : `Maximum: ${formatCurrency(balance, isAr)}`)
                                    : (isAr ? 'لا يوجد رصيد متاح للسحب حالياً' : 'No balance available to withdraw')}
                                disabled={hasPendingRequest || balance <= 0}
                            />
                            <TextField
                                select
                                label={isAr ? 'طريقة الاستلام' : 'Payout method'}
                                value={payoutMethod}
                                onChange={(e) => setPayoutMethod(e.target.value)}
                                size="small"
                                fullWidth
                                disabled={hasPendingRequest || balance <= 0}
                            >
                                <MenuItem value="bank_transfer">{isAr ? 'تحويل بنكي' : 'Bank transfer'}</MenuItem>
                                <MenuItem value="wallet">{isAr ? 'محفظة' : 'Wallet'}</MenuItem>
                                <MenuItem value="cash">{isAr ? 'نقداً' : 'Cash'}</MenuItem>
                            </TextField>
                            <TextField
                                label={isAr ? 'تفاصيل الاستلام' : 'Payout details'}
                                value={payoutDetails}
                                onChange={(e) => setPayoutDetails(e.target.value)}
                                size="small"
                                placeholder={isAr ? 'رقم الحساب، اسم البنك، رقم الهاتف...' : 'Account number, bank name, phone...'}
                                fullWidth
                                disabled={hasPendingRequest || balance <= 0}
                            />
                            <Button
                                variant="contained"
                                onClick={requestWithdraw}
                                disabled={sendWait || !amount || hasPendingRequest || balance <= 0}
                                sx={{ minHeight: 40, bgcolor: '#0D358C', '&:hover': { bgcolor: '#1d4ed8' } }}
                            >
                                {sendWait ? <CircularProgress size={20} color="inherit" /> : (language === 'ar' ? 'إرسال الطلب' : 'Submit request')}
                            </Button>
                    </Box>
                        <Typography variant="caption" color="text.secondary" className="!mt-2 block">
                            {language === 'ar' ? 'سيُراجع الأدمن الطلب وينفّذه من لوحة التحكم مع حفظ طريقة الاستلام وتفاصيلها.' : 'Admin will review the request and see the selected payout method and details.'}
                        </Typography>
                </DashboardPanelCard>

                <DashboardPanelCard
                    title={language === 'ar' ? 'طلبات السحب' : 'Withdrawal requests'}
                    subtitle={language === 'ar' ? 'متابعة حالة طلبات السحب السابقة وملاحظات الإدارة.' : 'Track previous withdrawal requests and any admin notes.'}
                    className="!mb-4 mx-0"
                >
                    {withdrawalRequests.length > 0 ? withdrawalRequests.map((req) => (
                        <Box
                            key={req.id}
                            sx={{
                                py: 1.75,
                                borderBottom: 1,
                                borderColor: 'divider',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                gap: 2,
                                '&:last-of-type': { borderBottom: 'none', pb: 0 },
                            }}
                            className="max-sm:flex-col"
                        >
                                    <Box>
                                        <Typography variant="body1" fontWeight={800} sx={{ color: '#0D358C' }}>
                                            {formatCurrency(req.amount, isAr)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" className="block" sx={{ mt: 0.25 }}>
                                            {formatDate(req.created_at, isAr)}
                                        </Typography>
                                        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            <Chip size="small" variant="outlined" label={getMethodLabel(req.payout_method)} />
                                            <Chip
                                                size="small"
                                                label={getStatusLabel(req.status)}
                                                color={
                                                    req.status === 'approved'
                                                        ? 'success'
                                                        : req.status === 'rejected'
                                                            ? 'error'
                                                            : 'warning'
                                                }
                                                variant="outlined"
                                            />
                                        </Box>
                                        {req.payout_details ? (
                                            <Typography variant="caption" color="text.secondary" className="block !mt-2">
                                                {req.payout_details}
                                            </Typography>
                                        ) : null}
                                        {req.admin_notes ? (
                                            <Typography variant="caption" color="text.secondary" className="block !mt-1">
                                                {isAr ? 'ملاحظات الإدارة:' : 'Admin notes:'} {req.admin_notes}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                </Box>
                    )) : (
                        <Typography color="text.secondary">
                            {isAr ? 'لا توجد طلبات سحب حالياً.' : 'No withdrawal requests yet.'}
                        </Typography>
                    )}
                </DashboardPanelCard>

                <DashboardPanelCard
                    title={language === 'ar' ? 'سجل الحركات' : 'Transaction history'}
                    subtitle={language === 'ar' ? 'آخر العمليات المالية المرتبطة بحسابك.' : 'Recent financial operations tied to your account.'}
                    className="mx-0"
                >
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#f8fafc' }}>
                                    <TableCell>#</TableCell>
                                    <TableCell>{language === 'ar' ? 'النوع' : 'Type'}</TableCell>
                                    <TableCell>{language === 'ar' ? 'المبلغ' : 'Amount'}</TableCell>
                                    <TableCell>{language === 'ar' ? 'التاريخ' : 'Date'}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {transactions.length > 0 ? transactions.map((tx) => (
                                    <TableRow key={tx.id}>
                                        <TableCell>{tx.id}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={getTransactionTypeLabel(tx.type)}
                                                color={getTransactionTypeColor(tx.type)}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontWeight: 700,
                                                color:
                                                    tx.type === 'credit'
                                                        ? 'success.main'
                                                        : tx.type === 'debit'
                                                            ? 'error.main'
                                                            : 'warning.main',
                                            }}
                                        >
                                            {formatCurrency(tx.amount, isAr)}
                                        </TableCell>
                                        <TableCell>{formatDate(tx.created_at, isAr)}</TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center">
                                            <Typography color="text.secondary" sx={{ py: 3 }}>
                                                {isAr ? 'لا توجد حركات مالية بعد.' : 'No financial transactions yet.'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DashboardPanelCard>
                <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
            </PageContent>
        </>
    );
}

export default Financial;
